import {
  ActionIcon,
  FloatingWindow,
  Group,
  getGradient,
  Image,
  Paper,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core"
import {
  IconChevronDown,
  IconChevronUp,
  IconEyeOff,
  IconHeart,
  IconHeartFilled,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerSkipBack,
  IconPlayerSkipForward,
  IconX,
} from "@tabler/icons-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useAuth, useUsers } from "@/hooks"
import type { TRadioTrackInteraction } from "@/types"
import { radioAPI } from "@/utils"
import { UserAvatar } from "../people"
import { useRadioPlayer } from "./RadioPlayerProvider"
import { feedListKey, useRadioTrackMutation } from "./useRadioTrackMutation"
import { YouTubePlayer } from "./YouTubePlayer"
import { extractYouTubeVideoId } from "./youtube"

export function RadioPlayer() {
  const theme = useMantineTheme()
  const { currentTrack, isPlaying, isExpanded, next } = useRadioPlayer()
  const videoId = currentTrack ? extractYouTubeVideoId(currentTrack.url) : null
  const { findUser } = useUsers()

  if (!currentTrack || !videoId) return null

  const user = findUser(currentTrack.user_id)

  return (
    <FloatingWindow
      shadow="xl"
      p={16}
      w={442}
      withBorder
      style={{ cursor: "move" }}
      initialPosition={{ top: 20, right: 20 }}
      bg={getGradient({ from: "indigo.1", to: "cyan.1", deg: 180 }, theme)}
      data-testid="radio-player"
    >
      <PlayerBar />
      <Stack gap="sm">
        <TrackInfo />

        <Paper h={isExpanded ? 380 : 0} style={{ overflow: "hidden" }}>
          <YouTubePlayer videoId={videoId} isPlaying={isPlaying} onEnd={next} />
        </Paper>

        <PlayerControls />
      </Stack>
      {user && <UserAvatar user={user} size={32} radius="xl" pos="absolute" bottom={14} right={12} />}
    </FloatingWindow>
  )
}

function TrackInfo() {
  const { currentTrack } = useRadioPlayer()

  if (!currentTrack) return null

  return (
    <Group gap="sm" wrap="nowrap" maw={300}>
      {currentTrack.thumbnail && (
        <Image src={currentTrack.thumbnail} alt={currentTrack.title ?? ""} w={48} h={48} radius="sm" />
      )}
      <Stack gap={0}>
        <Text fw={500} lineClamp={1}>
          {currentTrack.title ?? "YouTube"}
        </Text>
        {currentTrack.artist && (
          <Text size="sm" c="dimmed" lineClamp={1}>
            {currentTrack.artist}
          </Text>
        )}
      </Stack>
    </Group>
  )
}

function PlayerBar() {
  const { t } = useTranslation()
  const { currentTrack, setQueue, queue, next, close, isExpanded, toggleExpand } = useRadioPlayer()

  const { user } = useAuth()
  const isOwnTrack = currentTrack?.user_id === user?.id
  const queryClient = useQueryClient()

  const like = useRadioTrackMutation(currentTrack, "liked", radioAPI.toggleLike, {
    invalidateOnSettled: false,
    onMutate: (updated) => {
      setQueue(queue.map((t) => (t.id === updated.id ? updated : t)))
    },
  })

  const hide = useMutation({
    mutationFn: () =>
      currentTrack
        ? radioAPI.toggleHide(currentTrack.id)
        : (Promise.resolve(null) as unknown as Promise<TRadioTrackInteraction>),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: feedListKey })
      next()
    },
  })

  if (!currentTrack) return null

  return (
    <Group gap={4} wrap="nowrap" pos="absolute" top={8} right={8}>
      <Tooltip zIndex={2000} label={currentTrack.liked ? t("Unlike") : t("Like")}>
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={() => like.mutate()}
          loading={like.isPending}
          aria-label={t("Like")}
        >
          {currentTrack.liked ? <IconHeartFilled size={18} /> : <IconHeart size={18} />}
        </ActionIcon>
      </Tooltip>
      {!isOwnTrack && (
        <Tooltip zIndex={2000} label={t("Hide")}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => hide.mutate()}
            loading={hide.isPending}
            aria-label={t("Hide")}
          >
            <IconEyeOff size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip zIndex={2000} label={isExpanded ? t("Collapse") : t("Expand")}>
        <ActionIcon variant="subtle" onClick={toggleExpand} aria-label={isExpanded ? t("Collapse") : t("Expand")}>
          {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip zIndex={2000} label={t("Close")}>
        <ActionIcon variant="subtle" onClick={close} aria-label={t("Close")}>
          <IconX size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  )
}

function PlayerControls() {
  const { t } = useTranslation()
  const { isPlaying, resume, pause, next, prev } = useRadioPlayer()

  return (
    <Group justify="center" gap="md">
      <Tooltip zIndex={2000} label={t("Previous")}>
        <ActionIcon variant="light" onClick={prev} aria-label={t("Previous")}>
          <IconPlayerSkipBack size={20} />
        </ActionIcon>
      </Tooltip>
      <Tooltip zIndex={2000} label={isPlaying ? t("Pause") : t("Play")}>
        <ActionIcon
          variant="filled"
          onClick={() => (isPlaying ? pause() : resume())}
          aria-label={isPlaying ? t("Pause") : t("Play")}
        >
          {isPlaying ? <IconPlayerPause size={20} /> : <IconPlayerPlay size={20} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip zIndex={2000} label={t("Next")}>
        <ActionIcon variant="light" onClick={next} aria-label={t("Next")}>
          <IconPlayerSkipForward size={20} />
        </ActionIcon>
      </Tooltip>
    </Group>
  )
}
